/**
 * Voice AI — Shared IVR Styles
 * Common CSS for all IVR sub-components (grid, detail, tabs).
 * Uses glass CSS variables from design-system.js for consistency.
 * Pattern: agent-styles.js
 */

import { css } from 'lit';

export const ivrStyles = css`
  :host {
    display: block;
  }

  /* ─── Page Header ─── */
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-8);
  }

  .page-header-text {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .page-title {
    font-family: var(--font-display);
    font-size: var(--text-4xl);
    font-weight: var(--font-black);
    color: var(--color-text-primary);
    letter-spacing: -0.04em;
  }

  .page-description {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  /* ─── View Toggle (grid/list — matches agent-styles) ─── */
  .view-toggle {
    display: flex;
    background: var(--color-bg-tertiary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    overflow: hidden;
  }

  .view-toggle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 34px;
    border: none;
    background: transparent;
    color: var(--color-text-tertiary);
    cursor: pointer;
    transition: all var(--transition-fast);
    font-size: var(--text-sm);
  }

  .view-toggle-btn:hover {
    color: var(--color-text-secondary);
    background: var(--color-bg-hover);
  }

  .view-toggle-btn.active {
    background: var(--color-bg-secondary);
    color: var(--color-accent);
    box-shadow: var(--shadow-xs);
  }

  /* ─── Search + Filters (matches agent-styles) ─── */
  .search-filters {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-6);
    flex-wrap: wrap;
  }

  .filter-pills {
    display: flex;
    gap: var(--space-1);
    background: var(--color-bg-tertiary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    padding: 2px;
  }

  .filter-pill {
    padding: var(--space-1) var(--space-3);
    border: none;
    background: transparent;
    border-radius: var(--radius-sm);
    font-family: var(--font-body);
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    color: var(--color-text-tertiary);
    cursor: pointer;
    transition: all var(--transition-fast);
    white-space: nowrap;
  }

  .filter-pill:hover {
    color: var(--color-text-secondary);
  }

  .filter-pill.active {
    background: var(--color-bg-secondary);
    color: var(--color-accent);
    box-shadow: var(--shadow-xs);
  }

  .results-count {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    margin-left: auto;
  }

  /* ─── IVR Cards Grid ─── */
  .ivr-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-6);
    animation: slideUp var(--duration-slow) var(--ease-out) both;
  }

  .ivr-grid.list-view {
    grid-template-columns: 1fr;
  }

  .ivr-grid.list-view .ivr-card {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: var(--space-5);
    align-items: center;
  }

  .ivr-grid.list-view .ivr-stats-row {
    flex-direction: row;
  }

  .ivr-grid.list-view .ivr-card-footer {
    justify-content: flex-end;
    border-top: none;
    padding-top: 0;
  }

  .ivr-card {
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
    cursor: pointer;
    transition: all var(--transition-base);
    box-shadow: var(--glass-shadow);
    position: relative;
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    overflow: hidden;
  }

  .ivr-card:hover {
    box-shadow: var(--shadow-hover-lift);
    border-color: var(--color-border-secondary);
    transform: scale(1.01);
  }

  .ivr-card.sk-card {
    cursor: default;
    pointer-events: none;
  }

  .ivr-card-header {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    margin-bottom: var(--space-4);
  }

  .ivr-icon {
    width: 44px;
    height: 44px;
    border-radius: var(--radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-lg);
    color: var(--color-text-inverse);
    flex-shrink: 0;
    background: var(--color-primary);
    box-shadow: 0 2px 8px rgba(45, 143, 111, 0.2);
  }

  .ivr-icon.active {
    background: var(--color-success);
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.25);
  }

  .ivr-icon.draft {
    background: var(--color-warning);
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.25);
  }

  .ivr-icon.inactive {
    background: var(--color-text-tertiary);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .ivr-info {
    flex: 1;
    min-width: 0;
  }

  .ivr-name {
    font-family: var(--font-display);
    font-size: var(--text-base);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
    letter-spacing: var(--tracking-tight);
    line-height: var(--leading-tight);
    margin-bottom: var(--space-1);
  }

  .ivr-meta {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .ivr-stats-row {
    display: flex;
    gap: var(--space-4);
    margin-bottom: var(--space-3);
  }

  .ivr-stat-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
  }

  .stat-value {
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-text-primary);
  }

  .stat-label {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    letter-spacing: var(--tracking-wide);
  }

  .ivr-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: var(--space-3);
    border-top: 1px solid var(--color-border-subtle);
  }

  .ivr-card-footer .footer-desc {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  .ivr-card-footer .footer-voice {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-secondary);
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  /* Badges + status-dot: uses global componentStyles (same as agent page) */

  /* ─── Detail Panel ─── */
  .detail-panel {
    animation: slideUp var(--duration-slow) var(--ease-out);
  }

  .detail-back-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    background: none;
    border: none;
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    cursor: pointer;
    padding: 0;
    margin-bottom: var(--space-6);
    transition: color var(--transition-fast);
  }

  .detail-back-btn:hover {
    color: var(--color-text-primary);
  }

  .detail-header {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    margin-bottom: var(--space-6);
  }

  .detail-avatar {
    width: 56px;
    height: 56px;
    border-radius: var(--radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-xl);
    color: var(--color-text-inverse);
    flex-shrink: 0;
    background: var(--gradient-brand);
    box-shadow: 0 4px 12px rgba(45, 143, 111, 0.25);
  }

  .detail-title-area {
    flex: 1;
    min-width: 0;
  }

  .detail-title {
    font-family: var(--font-display);
    font-size: var(--text-2xl);
    font-weight: var(--font-black);
    color: var(--color-text-primary);
    letter-spacing: -0.03em;
    margin-bottom: var(--space-1);
  }

  .detail-subtitle {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  /* ─── Tabs ─── */
  .detail-tabs {
    display: flex;
    gap: var(--space-1);
    border-bottom: 1px solid var(--glass-divider);
    margin-bottom: var(--space-6);
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .detail-tabs::-webkit-scrollbar {
    display: none;
  }

  .detail-tab {
    padding: var(--space-3) var(--space-4);
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-tertiary);
    cursor: pointer;
    transition: all var(--transition-fast);
    white-space: nowrap;
  }

  .detail-tab:hover {
    color: var(--color-text-secondary);
  }

  .detail-tab.active {
    color: var(--color-primary);
    border-bottom-color: var(--color-primary);
    background: rgba(45, 143, 111, 0.06);
  }

  .detail-tab i {
    margin-right: var(--space-1);
    font-size: 11px;
  }

  /* ─── Section Containers (glassmorphism) ─── */
  .section {
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
    margin-bottom: var(--space-6);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    box-shadow: var(--glass-shadow);
    position: relative;
    overflow: hidden;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-5);
  }

  .section-title {
    font-family: var(--font-display);
    font-size: var(--text-lg);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
    letter-spacing: var(--tracking-tight);
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .section-title i {
    font-size: var(--text-base);
    color: var(--color-primary);
  }

  .section-description {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    margin-top: var(--space-1);
  }

  /* ─── Actions Footer ─── */
  .actions-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: var(--space-4);
    padding-top: var(--space-4);
    border-top: 1px solid var(--glass-divider);
  }

  .actions-footer-end {
    display: flex;
    gap: var(--space-3);
  }

  /* ─── Toggle Row (label + toggle) ─── */
  .toggle-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-top: var(--space-2);
  }

  .toggle-label {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
  }

  /* ─── Type Switcher (greeting/prompt type selector) ─── */
  .type-switcher {
    display: flex;
    gap: var(--space-1);
    background: var(--glass-bg-light);
    border: 1px solid var(--glass-divider);
    border-radius: var(--radius-lg);
    padding: 3px;
    width: fit-content;
    margin-bottom: var(--space-4);
  }

  .type-switcher-btn {
    padding: 6px 16px;
    background: transparent;
    border: none;
    border-radius: var(--radius-md);
    font-family: var(--font-body);
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    color: var(--color-text-tertiary);
    cursor: pointer;
    transition: all var(--transition-fast);
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  .type-switcher-btn:hover {
    color: var(--color-text-secondary);
    background: var(--glass-bg-light);
  }

  .type-switcher-btn.active {
    background: var(--glass-bg-input);
    color: var(--color-primary);
    box-shadow: var(--shadow-sm);
    font-weight: var(--font-semibold);
  }

  /* ─── Option Row ─── */
  .option-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    background: var(--glass-bg-light);
    border: 1px solid var(--glass-border-light);
    border-radius: var(--radius-lg);
    margin-bottom: var(--space-2);
    transition: all var(--transition-fast);
  }

  .option-row:hover {
    background: var(--glass-bg-light-hover);
    border-color: var(--glass-border-hover);
  }

  .option-digit {
    width: 36px;
    height: 36px;
    border-radius: var(--radius-md);
    background: var(--glass-bg-light-hover);
    border: 1px solid var(--glass-border-light);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
    flex-shrink: 0;
  }

  .option-info {
    flex: 1;
    min-width: 0;
  }

  .option-label {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-text-primary);
  }

  .option-action {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  .option-actions {
    display: flex;
    gap: var(--space-1);
  }

  .option-actions button {
    width: 30px;
    height: 30px;
    border: none;
    background: transparent;
    color: var(--color-text-tertiary);
    cursor: pointer;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all var(--transition-fast);
    font-size: var(--text-xs);
  }

  .option-actions button:hover {
    background: var(--glass-bg-light);
    color: var(--color-text-primary);
  }

  .option-actions button.danger:hover {
    color: var(--color-error);
    background: rgba(239, 68, 68, 0.1);
  }

  /* ─── Audio Player ─── */
  .audio-card {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    background: var(--glass-bg-light);
    border: 1px solid var(--glass-border-light);
    border-radius: var(--radius-lg);
    margin-bottom: var(--space-2);
    transition: all var(--transition-fast);
  }

  .audio-card:hover {
    background: var(--glass-bg-light-hover);
    border-color: var(--glass-border-hover);
  }

  .audio-icon {
    width: 36px;
    height: 36px;
    border-radius: var(--radius-md);
    background: rgba(45, 143, 111, 0.1);
    color: var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-sm);
    flex-shrink: 0;
  }

  .audio-info {
    flex: 1;
    min-width: 0;
  }

  .audio-name {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .audio-meta {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  .audio-actions {
    display: flex;
    gap: var(--space-1);
  }

  /* ─── Upload Zone ─── */
  .upload-zone {
    border: 2px dashed var(--glass-border);
    border-radius: var(--radius-xl);
    padding: var(--space-8);
    text-align: center;
    cursor: pointer;
    transition: all var(--transition-fast);
    background: rgba(255, 255, 255, 0.06);
  }

  .upload-zone:hover,
  .upload-zone.drag-over {
    border-color: var(--color-primary);
    background: rgba(45, 143, 111, 0.06);
  }

  .upload-zone-icon {
    font-size: var(--text-3xl);
    color: var(--color-text-tertiary);
    margin-bottom: var(--space-3);
  }

  .upload-zone-text {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
    margin-bottom: var(--space-1);
  }

  .upload-zone-hint {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  /* ─── Extension Table ─── */
  .ext-table {
    width: 100%;
    border-collapse: collapse;
  }

  .ext-table th {
    text-align: left;
    padding: var(--space-2) var(--space-3);
    font-family: var(--font-body);
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid var(--glass-divider);
  }

  .ext-table td {
    padding: var(--space-3);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-primary);
    border-bottom: 1px solid var(--glass-divider);
  }

  .ext-table tr:last-child td {
    border-bottom: none;
  }

  .ext-table tr:hover td {
    background: rgba(255, 255, 255, 0.06);
  }

  .ext-number {
    font-family: var(--font-mono);
    font-weight: var(--font-bold);
    color: var(--color-primary);
  }

  /* ─── Schedule Grid ─── */
  .schedule-day-row {
    display: grid;
    grid-template-columns: 48px 100px 1fr;
    gap: var(--space-3);
    align-items: center;
    padding: var(--space-2) var(--space-3);
    border-bottom: 1px solid var(--glass-divider);
    border-radius: var(--radius-md);
    transition: background var(--transition-fast);
  }

  .schedule-day-row:last-child {
    border-bottom: none;
  }

  .schedule-day-row:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  .schedule-day-row.disabled .schedule-day-label,
  .schedule-day-row.disabled .schedule-times {
    opacity: 0.4;
    pointer-events: none;
  }

  .schedule-day-label {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-text-primary);
  }

  .schedule-times {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .schedule-times input[type="time"] {
    padding: 6px 10px;
    background: var(--glass-bg-input);
    border: 1px solid var(--glass-border);
    border-radius: var(--radius-md);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    color: var(--color-text-primary);
    transition: all var(--transition-fast);
  }

  .schedule-times input[type="time"]:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: var(--shadow-glow-primary);
  }

  .schedule-times span {
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  /* ─── Section Disabled State ─── */
  .section-content.disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  /* ─── Route Toggle ─── */
  .route-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    background: var(--glass-bg-light);
    border: 1px solid var(--glass-border-light);
    border-radius: var(--radius-lg);
    margin-bottom: var(--space-2);
    transition: all var(--transition-fast);
  }

  .route-item:hover {
    background: var(--glass-bg-light-hover);
    border-color: var(--glass-border-hover);
  }

  .route-info {
    flex: 1;
    min-width: 0;
  }

  .route-number {
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
  }

  .route-current {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  /* ─── Empty State ─── */
  .empty-state {
    text-align: center;
    padding: var(--space-12) var(--space-8);
  }

  .empty-state-icon {
    font-size: var(--text-5xl);
    color: var(--color-text-muted);
    margin-bottom: var(--space-4);
    opacity: 0.5;
  }

  .empty-state-title {
    font-family: var(--font-display);
    font-size: var(--text-xl);
    font-weight: var(--font-bold);
    color: var(--color-text-secondary);
    margin-bottom: var(--space-2);
  }

  .empty-state-description {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    max-width: 360px;
    margin: 0 auto;
  }

  /* ─── Skeleton ─── */
  .skeleton-line {
    background: linear-gradient(90deg, var(--color-bg-tertiary) 25%, var(--color-bg-hover) 50%, var(--color-bg-tertiary) 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: var(--radius-sm);
    height: 12px;
  }

  .skeleton-line.circle {
    width: 44px;
    height: 44px;
    border-radius: var(--radius-full);
    flex-shrink: 0;
  }

  .skeleton-line.xs { height: 10px; }

  /* ─── Responsive ─── */
  @media (max-width: 1100px) {
    .ivr-grid { grid-template-columns: repeat(2, 1fr); }
  }

  @media (max-width: 700px) {
    .ivr-grid { grid-template-columns: 1fr; }
    .page-header { flex-direction: column; align-items: flex-start; gap: var(--space-4); }
    .search-filters { flex-direction: column; align-items: stretch; }
    .search-input-wrap { max-width: none; }
    .schedule-day-row { grid-template-columns: 100px 50px 1fr; }
  }
`;
